import { useEffect, useState } from "react";
import { api, auth as authApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import AppShell from "./AppShell";

export default function Settings() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data || {}));
  }, []);

  const update = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile", {
        industry: profile.industry || null,
        target_market: profile.target_market || null,
        business_location: profile.business_location || null,
        business_goals: profile.business_goals || null,
        company_size: profile.company_size || null,
        weekly_digest_optin: profile.weekly_digest_optin !== false,
      });
      toast.success("Saved.");
    } catch {
      toast.error("Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const sendTestDigest = async () => {
    setDigestBusy(true);
    try {
      const r = await api.post("/admin/digest/run-for-me");
      if (r.data?.ok) toast.success("Test digest sent (check your inbox / server logs).");
      else toast.error("No completed audit yet — run an audit first.");
    } catch {
      toast.error("Couldn't send test digest");
    } finally {
      setDigestBusy(false);
    }
  };

  const signOutAllDevices = async () => {
    setSigningOutAll(true);
    try {
      await authApi.logoutAll();
      toast.success("Signed out of all devices.");
      window.location.href = "/";
    } catch {
      toast.error("Couldn't sign out everywhere");
      setSigningOutAll(false);
    }
  };

  if (!profile) {
    return <AppShell title="Settings"><div className="font-mono text-sm">Loading…</div></AppShell>;
  }

  const digestOn = profile.weekly_digest_optin !== false; // default true

  return (
    <AppShell title="Settings" subtitle="// your account">
      <div className="max-w-3xl space-y-10" data-testid="settings-page">
        <section>
          <h2 className="font-marketing text-2xl">Account</h2>
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <dt className="text-muted-foreground">Email</dt>
            <dd className="font-mono">{user?.email}</dd>
            <dt className="text-muted-foreground">Business</dt>
            <dd className="font-mono">{user?.business_name}</dd>
            <dt className="text-muted-foreground">Website</dt>
            <dd className="font-mono">{user?.website_url}</dd>
          </dl>
        </section>

        <section>
          <h2 className="font-marketing text-2xl">Business profile</h2>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Industry</label>
              <input value={profile.industry || ""} onChange={(e) => update("industry", e.target.value)} data-testid="settings-industry-input" className="mt-1 w-full bg-card border border-border px-4 py-3 text-sm" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Company size</label>
              <input value={profile.company_size || ""} onChange={(e) => update("company_size", e.target.value)} data-testid="settings-size-input" className="mt-1 w-full bg-card border border-border px-4 py-3 text-sm" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Target market</label>
              <input value={profile.target_market || ""} onChange={(e) => update("target_market", e.target.value)} data-testid="settings-target-input" className="mt-1 w-full bg-card border border-border px-4 py-3 text-sm" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Location</label>
              <input value={profile.business_location || ""} onChange={(e) => update("business_location", e.target.value)} data-testid="settings-location-input" className="mt-1 w-full bg-card border border-border px-4 py-3 text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Business goals</label>
              <textarea rows={3} value={profile.business_goals || ""} onChange={(e) => update("business_goals", e.target.value)} data-testid="settings-goals-input" className="mt-1 w-full bg-card border border-border px-4 py-3 text-sm" />
            </div>
          </div>
          <button
            onClick={save}
            disabled={busy}
            data-testid="settings-save-button"
            className="mt-6 px-5 py-2.5 bg-obsidian text-bone font-app text-xs uppercase tracking-widest font-bold hover:bg-intl transition-colors disabled:opacity-60"
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </section>

        <section>
          <h2 className="font-marketing text-2xl">Email notifications</h2>
          <div className="mt-4 border border-border bg-card p-5">
            <label className="flex items-start gap-4 cursor-pointer" data-testid="digest-optin-label">
              <input
                type="checkbox"
                checked={digestOn}
                data-testid="digest-optin-checkbox"
                onChange={(e) => update("weekly_digest_optin", e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="font-app font-medium">Weekly recap email</span>
                <span className="block text-sm text-muted-foreground mt-1">
                  Every Monday, get a one-glance recap of your score, week-over-week delta and the top unfinished recommendation.
                </span>
              </span>
            </label>
            <div className="mt-4 flex gap-3">
              <button
                onClick={save}
                disabled={busy}
                data-testid="digest-save-button"
                className="px-4 py-2 bg-obsidian text-bone font-app text-xs uppercase tracking-widest font-bold hover:bg-intl transition-colors disabled:opacity-60"
              >
                {busy ? "Saving…" : "Save preference"}
              </button>
              <button
                onClick={sendTestDigest}
                disabled={digestBusy || !digestOn}
                data-testid="digest-test-button"
                className="px-4 py-2 border border-border font-app text-xs uppercase tracking-widest font-bold hover:bg-obsidian hover:text-bone transition-colors disabled:opacity-50"
              >
                {digestBusy ? "Sending…" : "Send test digest now"}
              </button>
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-marketing text-2xl">Sessions</h2>
          <p className="mt-2 text-muted-foreground text-sm">
            Sign out of every device you're currently logged into. We'll revoke every active session immediately.
          </p>
          <button
            onClick={signOutAllDevices}
            disabled={signingOutAll}
            data-testid="settings-logout-all-button"
            className="mt-4 px-5 py-2.5 border border-border font-app text-xs uppercase tracking-widest font-bold hover:bg-obsidian hover:text-bone transition-colors disabled:opacity-60"
          >
            {signingOutAll ? "Revoking…" : "Sign out everywhere"}
          </button>
        </section>

        <section>
          <h2 className="font-marketing text-2xl">Danger zone</h2>
          <p className="mt-2 text-muted-foreground text-sm">Sign out of this device.</p>
          <button onClick={logout} data-testid="settings-logout-button" className="mt-4 px-5 py-2.5 border border-border font-app text-xs uppercase tracking-widest font-bold hover:bg-obsidian hover:text-bone transition-colors">
            Logout
          </button>
        </section>
      </div>
    </AppShell>
  );
}
